"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { db, auth } from "../../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { updateProfile } from "firebase/auth";
import {
  User,
  Mail,
  Calendar,
  Trophy,
  X,
  ShieldAlert,
  Loader2,
  Terminal,
  Hash,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { useModalA11y } from "@/utils/useModalA11y";

// --- Loading Skeleton Component ---
const ProfileSkeleton = () => (
  <div className="animate-pulse space-y-8">
    <div className="flex flex-col items-center">
      <div className="h-6 w-32 bg-zinc-800 rounded-sm mb-4" />
      <div className="flex flex-col md:flex-row gap-4">
        <div className="h-3 w-24 bg-zinc-800 rounded-sm" />
        <div className="h-3 w-24 bg-zinc-800 rounded-sm" />
      </div>
    </div>
    <div className="space-y-4">
      <div className="h-3 w-20 bg-zinc-800 rounded-sm" />
      <div className="grid grid-cols-2 gap-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-12 bg-zinc-900 border border-zinc-800/50 rounded-sm" />
        ))}
      </div>
    </div>
    <div className="h-24 bg-zinc-900 border border-zinc-800 rounded-sm" />
  </div>
);

export default function ProfileModal({ isOpen, onClose, user }) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [showAllStats, setShowAllStats] = useState(false);
  const dialogRef = useRef(null);

  /* ---------------- MODAL LIFECYCLE ---------------- */
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else if (mounted) {
      setVisible(false);
      const timer = setTimeout(() => {
        setMounted(false);
        setIsEditing(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, mounted]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  useModalA11y(mounted, handleClose, dialogRef);

  const mmrData = useMemo(() => {
    if (!userData) return [];
    return Object.entries(userData)
      .filter(([key, value]) => key.startsWith("mmr_") && typeof value === 'number')
      .map(([key, value]) => {
        const parts = key.replace("mmr_", "").split("_");
        let label = parts[0] || ""; 
        if (parts[1]) label += ` ${parts[1]}`;
        if (parts.includes("punc")) label += " +punc";
        if (parts.includes("nums")) label += " +nums";
        return { label: label, value: value };
      })
      .sort((a, b) => b.value - a.value);
  }, [userData]);

  /* ---------------- MIGRATE LEGACY USERS ---------------- */
  useEffect(() => {
    if (userData && !userData.displayId && user?.uid) {
      const newId = (self.crypto && self.crypto.randomUUID) ? self.crypto.randomUUID() : Math.random().toString(36).substring(2, 15);
      updateDoc(doc(db, "users", user.uid), { displayId: newId })
        .then(() => setUserData(prev => ({ ...prev, displayId: newId })))
        .catch(console.error);
    }
  }, [userData, user?.uid]);

  const displayId = userData?.displayId || "................";

  /* ---------------- FETCH USER DATA ---------------- */
  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.uid && isOpen) {
        setLoading(true);
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData(data);
            setNewDisplayName(user.displayName || data.username || "");
          }
        } catch (err) {
          console.error("Error fetching user data:", err);
        } finally {
          setTimeout(() => setLoading(false), 500);
        }
      }
    };

    if (isOpen && !isEditing) fetchUserData();
  }, [user, isOpen, isEditing]);

  const handleCancel = () => {
    setIsEditing(false);
    setError("");
    setNewDisplayName(user?.displayName || userData?.username || "");
  };

  /* ---------------- SAVE PROFILE ---------------- */
  const handleSaveChanges = async () => {
    if (!user || !auth.currentUser) return;
    setIsSaving(true);
    setError("");
    try {
      const profileUpdates = {};
      const userDocUpdates = {};

      if (newDisplayName && newDisplayName.trim() && newDisplayName !== (user.displayName || userData?.username)) {
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("username", "==", newDisplayName.trim()));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty && querySnapshot.docs.some((d) => d.id !== user.uid)) {
          throw new Error("This nickname is already taken.");
        }

        profileUpdates.displayName = newDisplayName.trim();
        userDocUpdates.username = newDisplayName.trim();
      }

      if (Object.keys(profileUpdates).length > 0) await updateProfile(auth.currentUser, profileUpdates);
      if (Object.keys(userDocUpdates).length > 0) await updateDoc(doc(db, "users", user.uid), userDocUpdates);

      setIsEditing(false);
    } catch (err) {
      setError(err.message.toUpperCase());
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "UNKNOWN";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).toUpperCase();
  };

  if (!mounted) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-[100] flex items-start justify-center p-4 md:p-8 bg-zinc-950/95 backdrop-blur-md transition-all duration-300 overflow-y-auto scrollbar-none",
        visible ? "opacity-100" : "opacity-0"
      )}
      onClick={handleClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Profile"
        className={cn(
          "w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden transform transition-all duration-500 ease-out my-auto",
          visible ? "scale-100 translate-y-0" : "scale-95 translate-y-8"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Banner Section */}
        <div className="h-32 bg-zinc-950 w-full relative group border-b border-zinc-800/50">
          <img
            src={userData?.coverPhotoURL}
            alt="Cover"
            className={cn(
              "w-full h-full object-cover opacity-40 transition-opacity group-hover:opacity-60",
              !userData?.coverPhotoURL && "hidden"
            )}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent" />
          
          <button
            onClick={handleClose}
            aria-label="Close dialog"
            className="absolute top-4 right-4 p-1.5 text-zinc-500 hover:text-white transition-colors bg-zinc-900/80 border border-zinc-800 rounded z-10"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 md:px-8 pb-8">
          {/* Profile Picture */}
          <div className="relative -mt-12 mb-8 flex justify-center">
            <div className="w-24 h-24 rounded-sm bg-zinc-950 border-2 border-zinc-800 flex items-center justify-center overflow-hidden relative group shadow-xl">
              <img
                src={user?.photoURL}
                alt="Profile"
                className={cn(
                  "w-full h-full object-cover",
                  !user?.photoURL && "hidden"
                )}
              />
              {!user?.photoURL && (
                <div className="w-full h-full bg-zinc-950 flex items-center justify-center text-zinc-800">
                  <User size={32} />
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <ProfileSkeleton />
          ) : (
            <div className="animate-in fade-in duration-500">
              {/* Identity Display */}
              <div className="mb-8">
                {isEditing ? (
                  <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                    {error && (
                      <div className="p-3 bg-red-500/5 border-l-2 border-red-500 text-red-500 text-[10px] font-mono font-bold uppercase">
                        SYSTEM_ERR: {error}
                      </div>
                    )}
                    <div className="space-y-1.5 text-left">
                      <label className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest ml-1">Nickname_Input</label>
                      <input
                        type="text"
                        value={newDisplayName}
                        onChange={(e) => setNewDisplayName(e.target.value)}
                        className="w-full p-4 bg-zinc-950 border border-zinc-800 rounded-sm text-white text-sm font-mono focus:border-indigo-500 outline-none transition-all placeholder:text-zinc-800"
                        placeholder="ENTER_ID"
                        autoFocus
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter font-mono leading-none">
                      {user?.displayName || userData?.username || "GUEST_UNKN"}
                    </h2>
                    <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 mt-4">
                      <div className="flex items-center gap-1.5 text-zinc-500 font-mono text-[9px] uppercase tracking-tighter">
                        <Mail size={12} className="text-zinc-700" />
                        {user?.email}
                      </div>
                      <div className="hidden md:block w-1 h-1 bg-zinc-800 rounded-full" />
                      <div className="flex items-center gap-1.5 text-zinc-500 font-mono text-[9px] uppercase tracking-tighter">
                        <Calendar size={12} className="text-zinc-700" />
                        JOINED_{formatDate(user?.metadata?.creationTime)}
                      </div>
                      <div className="hidden md:block w-1 h-1 bg-zinc-800 rounded-full" />
                      <div className="flex items-center gap-1.5 text-zinc-500 font-mono text-[9px] uppercase tracking-tighter">
                        <Hash size={12} className="text-zinc-700" />
                        ID:{displayId.substring(0, 8)}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Stats Section */}
              {!isEditing && (
                <>
                  {mmrData.length > 0 && (
                    <div className="space-y-3 mb-8">
                      <div className="flex items-center gap-2 mb-4">
                        <Trophy size={14} className="text-amber-400" />
                        <span className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 font-mono italic">Ranked MMR</span>
                        <div className="h-px bg-zinc-800/30 flex-1 ml-2" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {(showAllStats ? mmrData : mmrData.slice(0, 4)).map((mmr) => (
                          <div key={mmr.label} className="bg-zinc-950 p-3 rounded border border-zinc-800/50 flex items-center justify-between group hover:border-indigo-500/30 transition-colors">
                            <span className="text-[9px] font-mono text-zinc-400 uppercase tracking-wider">{mmr.label}</span>
                            <span className="text-lg font-black font-mono text-indigo-400 italic">{mmr.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {mmrData.length > 4 && (
                    <button
                      onClick={() => setShowAllStats(!showAllStats)}
                      className="w-full py-2 mb-8 text-[9px] font-mono uppercase tracking-widest text-zinc-600 hover:text-zinc-400 transition-colors border border-dashed border-zinc-800 hover:border-zinc-700 rounded-sm"
                    >
                      {showAllStats ? "COLLAPSE_METRICS" : `VIEW_ALL_METRICS [${mmrData.length}]`}
                    </button>
                  )}

                  <div className="space-y-3 mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <Terminal size={14} className="text-indigo-400" />
                      <span className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 font-mono italic">Identity_Stats</span>
                      <div className="h-px bg-zinc-800/30 flex-1 ml-2" />
                    </div>
                    <div className="grid grid-cols-2 gap-px bg-zinc-800 border border-zinc-800 overflow-hidden rounded-sm">
                      <div className="bg-zinc-950 p-5 flex flex-col items-center group transition-colors hover:bg-zinc-900/50">
                        <div className="flex items-center gap-2 mb-1">
                          <Trophy size={10} className="text-zinc-700 group-hover:text-emerald-500" />
                          <span className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest">Victories</span>
                        </div>
                        <div className="text-3xl font-black font-mono text-white italic">
                          {(userData?.wins || 0).toString().padStart(2, '0')}
                        </div>
                      </div>
                      <div className="bg-zinc-950 p-5 flex flex-col items-center group transition-colors hover:bg-zinc-900/50">
                        <div className="flex items-center gap-2 mb-1">
                          <ShieldAlert size={10} className="text-zinc-700 group-hover:text-red-500" />
                          <span className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest">Defeats</span>
                        </div>
                        <div className="text-3xl font-black font-mono text-white italic">
                          {(userData?.losses || 0).toString().padStart(2, '0')}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="pt-6 border-t border-zinc-800/30">
                {isEditing ? (
                  <div className="flex gap-3">
                    <button
                      onClick={handleCancel}
                      disabled={isSaving}
                      className="flex-1 py-4 border border-zinc-800 text-zinc-600 hover:text-white hover:bg-zinc-800 font-black text-[10px] uppercase tracking-[0.2em] transition-all font-mono"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveChanges}
                      disabled={isSaving}
                      className="flex-1 py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-black text-[10px] uppercase tracking-[0.2em] transition-all font-mono italic flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 size={14} className="animate-spin" /> : "Save"}
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => setIsEditing(true)} 
                    className="w-full py-4 bg-zinc-950 border border-zinc-800 hover:bg-zinc-800 text-zinc-400 hover:text-white font-black text-[10px] uppercase tracking-[0.2em] transition-all font-mono italic"
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* System Footer */}
        <div className="sticky bottom-0 z-30 p-3 bg-zinc-900 border-t border-zinc-800/50 flex justify-between items-center px-6">
          <span className="text-[8px] font-mono text-zinc-700 uppercase tracking-[0.4em]"></span>
          <div className="flex gap-1.5">
            <div className="w-1 h-1 bg-zinc-800 rounded-full" />
            <div className="w-1 h-1 bg-zinc-800 rounded-full" />
            <div className="w-1 h-1 bg-indigo-600 rounded-full animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
}